import React, { useCallback } from 'react';
import useTranslate from '../../Hooks/useTranslate';
import useAPI from '../../api';
import FetchAutoComplete from '../Inputs/FetchAutoComplete';
import { useFieldParameters } from './binding/useFieldParameters';
import { BlockedField } from './binding/PointedFields';

// Options come back either as plain strings or as { value, label } rows,
// depending on the stored procedure behind the lookup key.
const optionLabel = (option) => {
  if (option === null || option === undefined) return '';
  if (typeof option === 'object')
    return String(option.label ?? option.value ?? '');
  return String(option);
};

const optionValue = (option) =>
  option !== null && typeof option === 'object'
    ? (option.value ?? option.label ?? null)
    : option;

/**
 * Type-ahead select for schema fields whose option list is too large to fetch
 * up front. Queries the DataSourceRegistry lookup key in `field.optionsUrl`,
 * passing the typed text as a parameter on every keystroke.
 *
 * Field properties read: `optionsUrl` (lookup key), `label`, `multiple`,
 * `searchParameter` (name of the search parameter, default `Search`),
 * `parameters` (extra parameters merged into every request), `contextParameters`
 * (names taken from the enclosing SchemaParametersProvider) and `dependsOn`
 * (siblings whose current values are merged in alongside them).
 *
 * A field waiting on a sibling it depends on is disabled until that sibling is
 * filled in, and clicking it points at the ones that are missing.
 */
export const AsyncSelect = ({ field, value, onChange, basePath = null }) => {
  const API = useAPI();
  const translate = useTranslate();
  const { parameters, waitingOn } = useFieldParameters(field, basePath, {
    value,
    onChange,
  });
  const blocked = waitingOn.length > 0;
  // Held as text so the callback below is rebuilt when a parameter's value changes rather than
  // whenever the object holding it is.
  const asked = JSON.stringify(parameters);

  const fetchOptions = useCallback(
    (searchValue, onSuccess, onFailure) =>
      API.lookupDynamicOptionsBySearch(
        field.optionsUrl,
        {
          ...JSON.parse(asked),
          [field.searchParameter || 'Search']: searchValue,
        },
        onSuccess,
        onFailure
      ),
    [field.optionsUrl, field.searchParameter, asked]
  );

  const handleChange = (event, newValue) =>
    onChange(
      Array.isArray(newValue)
        ? newValue.map(optionValue)
        : optionValue(newValue)
    );

  return (
    <BlockedField waitingOn={waitingOn}>
      <FetchAutoComplete
        id={`${field.key}-async-select`}
        label={translate(field.label)}
        width="100%"
        multiple={!!field.multiple}
        disabled={blocked}
        selectedValue={value ?? (field.multiple ? [] : null)}
        getOptionLabel={optionLabel}
        onChangeSelectedValue={handleChange}
        fetchCallback={fetchOptions}
      />
    </BlockedField>
  );
};
